import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  UploadCloud, 
  Trash2, 
  Eye, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  ZoomIn, 
  ZoomOut, 
  ExternalLink 
} from 'lucide-react';
import '../styles/account.css';
import { prepareBillImage } from '../utils/imageOptimizer';
import { apiFetch } from '../utils/api';


const AdminBills = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Auth Check
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  // Form states
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [userOrders, setUserOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  
  // Fetching/Loading states
  const [users, setUsers] = useState([]);
  const [bills, setBills] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingBills, setLoadingBills] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Pagination & Messages
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [totalBills, setTotalBills] = useState(0);
  const [message, setMessage] = useState(null);
  
  // Modal preview
  const [previewBill, setPreviewBill] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);
  
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch bills helper
  const fetchBills = useCallback(async (pageNum) => {
    try {
      setLoadingBills(true);
      const res = await apiFetch(`/api/admin/bills?page=${pageNum}&limit=10`);
      const data = await res.json();
      if (res.ok) {
        setBills(data.bills || []);
        setPage(data.page || 1);
        setPages(data.pages || 1);
        setTotalBills(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch admin bills:', err);
    } finally {
      setLoadingBills(false);
    }
  }, []);

  // Fetch users & bills on load
  useEffect(() => {
    if (!user) return;
    
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const res = await apiFetch('/api/auth/users');
        const data = await res.json();
        if (res.ok) {
          setUsers(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Failed to fetch users:', err);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
    fetchBills(1);
  }, [user, fetchBills]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch orders when customer is selected
  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setSearchQuery('');
    setShowDropdown(false);
    setSelectedOrder('');
    setUserOrders([]);

    try {
      setLoadingOrders(true);
      const res = await apiFetch(`/api/admin/orders/user/${customer._id}`);
      const data = await res.json();
      if (res.ok) {
        setUserOrders(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Failed to fetch orders for customer:', err);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Handle file select
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // MIME type validation
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Invalid file type. Only JPG, JPEG, PNG, and WEBP image formats are supported.' });
      return;
    }

    try {
      const preparedFile = await prepareBillImage(file);
      setImageFile(preparedFile);
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      setImagePreviewUrl(URL.createObjectURL(preparedFile));
      setMessage(null);
    } catch (err) {
      console.error('Bill image preparation failed, using original file:', err);
      setImageFile(file);
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      setImagePreviewUrl(URL.createObjectURL(file));
      setMessage(null);
    }
  };

  // Clean up object URL
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  // Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) {
      setMessage({ type: 'error', text: 'Please select a customer.' });
      return;
    }
    if (!invoiceNumber.trim()) {
      setMessage({ type: 'error', text: 'Please enter an invoice number.' });
      return;
    }
    if (!imageFile) {
      setMessage({ type: 'error', text: 'Please upload a bill image file.' });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('userId', selectedCustomer._id);
      formData.append('invoiceNumber', invoiceNumber.trim());
      if (selectedOrder) {
        formData.append('orderId', selectedOrder);
      }
      formData.append('image', imageFile);

      const res = await apiFetch('/api/admin/bills', {
        method: 'POST',
        body: formData
      });

      const data = await res.json();

      if (res.ok) {
        setMessage({ type: 'success', text: 'Invoice uploaded and linked successfully!' });
        // Reset Form
        setSelectedCustomer(null);
        setInvoiceNumber('');
        setSelectedOrder('');
        setUserOrders([]);
        setImageFile(null);
        if (imagePreviewUrl) {
          URL.revokeObjectURL(imagePreviewUrl);
          setImagePreviewUrl('');
        }
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Refresh Bills List
        fetchBills(1);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to upload bill.' });
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'An error occurred during upload.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Deletion
  const handleDeleteBill = async (billId) => {
    if (!window.confirm('Are you absolutely sure you want to delete this invoice? This will also remove the image file from storage.')) {
      return;
    }

    try {
      const res = await apiFetch(`/api/admin/bills/${billId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'Invoice successfully removed.' });
        // Fetch current page bills
        fetchBills(bills.length === 1 && page > 1 ? page - 1 : page);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to delete invoice.' });
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'An error occurred during deletion.' });
    }
  };

  // Autocomplete filtering
  const filteredUsers = searchQuery.trim() === '' 
    ? [] 
    : users.filter(u => 
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.shopName && u.shopName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase())
      );

  // Formatting date helper
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format bytes to readable string
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="admin-bills-container">
      {/* Title Header */}
      <div className="admin-bills-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '1.8rem' }}>🧾</span>
          <h2 style={{ margin: 0, color: 'var(--color-text-primary)' }}>Bill & Invoice Management</h2>
        </div>
        <p className="admin-bills-subtitle">Upload wholesale buyer invoices, view historical billing records, and manage billing data.</p>
      </div>

      {/* Message alerts */}
      {message && (
        <div className={`admin-alert ${message.type === 'error' ? 'alert-error' : 'alert-success'}`}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)} className="alert-close-btn">&times;</button>
        </div>
      )}

      <div className="admin-bills-grid-layout">
        
        {/* LEFT COLUMN: Upload Invoice Form */}
        <div className="admin-card upload-invoice-card">
          <h3 className="card-title">Upload New Invoice</h3>
          
          <form onSubmit={handleSubmit} className="admin-bill-form">
            
            {/* 1. Customer Search */}
            <div className="form-group dropdown-wrapper" ref={dropdownRef}>
              <label className="form-label">Customer Search <span className="req">*</span></label>
              
              {!selectedCustomer ? (
                <div className="search-input-wrapper">
                  <Search className="search-icon" size={18} />
                  <input
                    type="text"
                    className="form-input search-input"
                    placeholder="Search by customer name, shop name, or email..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                  />
                  {loadingUsers && <span className="input-loading-spinner" />}
                </div>
              ) : (
                <div className="selected-customer-box">
                  <div className="customer-info-detail">
                    <span className="customer-name-badge">{selectedCustomer.name}</span>
                    {selectedCustomer.shopName && (
                      <span className="customer-shop-badge">🏢 {selectedCustomer.shopName}</span>
                    )}
                    <span className="customer-email-badge">✉ {selectedCustomer.email}</span>
                  </div>
                  <button 
                    type="button" 
                    className="clear-customer-btn"
                    onClick={() => setSelectedCustomer(null)}
                    title="Change Customer"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              {/* Autocomplete Dropdown List */}
              {showDropdown && filteredUsers.length > 0 && (
                <ul className="customer-autocomplete-list">
                  {filteredUsers.map(u => (
                    <li 
                      key={u._id} 
                      onClick={() => handleSelectCustomer(u)}
                      className="customer-autocomplete-item"
                    >
                      <div className="autocomplete-user-title">{u.name}</div>
                      <div className="autocomplete-user-meta">
                        {u.shopName && <span className="shop-name">🏢 {u.shopName}</span>}
                        <span className="email-addr">✉ {u.email}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {showDropdown && searchQuery.trim() !== '' && filteredUsers.length === 0 && !loadingUsers && (
                <div className="customer-autocomplete-no-results">
                  No matching customers found
                </div>
              )}
            </div>

            {/* 2. Invoice Number */}
            <div className="form-group">
              <label className="form-label" htmlFor="invoiceNo">Invoice Number <span className="req">*</span></label>
              <input
                id="invoiceNo"
                type="text"
                className="form-input"
                placeholder="e.g. INV-2026-0089"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                required
              />
            </div>

            {/* 3. Link Order (Optional) */}
            <div className="form-group">
              <label className="form-label" htmlFor="orderSelect">Associate Order <span className="opt">(Optional)</span></label>
              <select
                id="orderSelect"
                className="form-input form-select"
                value={selectedOrder}
                onChange={(e) => setSelectedOrder(e.target.value)}
                disabled={!selectedCustomer || loadingOrders}
              >
                {!selectedCustomer ? (
                  <option value="">Select a customer first</option>
                ) : loadingOrders ? (
                  <option value="">Fetching user orders...</option>
                ) : userOrders.length === 0 ? (
                  <option value="">No orders found for this customer</option>
                ) : (
                  <>
                    <option value="">-- Do not link an order --</option>
                    {userOrders.map(ord => (
                      <option key={ord._id} value={ord._id}>
                        {ord.orderNumber || `Order #${ord._id.substring(18)}`} ({formatDate(ord.createdAt)})
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>

            {/* 4. Bill Image File Upload */}
            <div className="form-group">
              <label className="form-label">Upload Invoice Image <span className="req">*</span></label>
              
              <div 
                className={`file-upload-dropzone ${imageFile ? 'has-file' : ''}`}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleFileChange}
                />
                
                {!imagePreviewUrl ? (
                  <div className="dropzone-empty-content">
                    <UploadCloud size={40} className="upload-icon" />
                    <p className="upload-title">Choose file or drag & drop</p>
                    <p className="upload-constraints">Supports JPG, JPEG, PNG, WEBP files up to 10MB</p>
                  </div>
                ) : (
                  <div className="dropzone-preview-content" onClick={(e) => e.stopPropagation()}>
                    <img src={imagePreviewUrl} alt="Bill Preview" className="uploaded-image-preview" />
                    <div className="file-metadata-overlay">
                      <span className="file-name">{imageFile?.name}</span>
                      <span className="file-size">{imageFile ? formatBytes(imageFile.size) : ''}</span>
                    </div>
                    <button 
                      type="button" 
                      className="remove-file-btn" 
                      onClick={() => {
                        setImageFile(null);
                        setImagePreviewUrl('');
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn btn-block" 
              disabled={submitting || !selectedCustomer || !invoiceNumber || !imageFile}
              style={{ marginTop: '10px' }}
            >
              {submitting ? 'Uploading to Storage...' : 'Upload & Save Invoice'}
            </button>

          </form>
        </div>

        {/* RIGHT COLUMN: paginated invoice listings */}
        <div className="admin-card bills-directory-card">
          <h3 className="card-title">Invoices Directory ({totalBills})</h3>

          {loadingBills && bills.length === 0 ? (
            <div className="skeleton-table-container">
              <div className="skeleton skeleton-row" style={{ height: '40px', marginBottom: '15px' }} />
              <div className="skeleton skeleton-row" style={{ height: '55px', marginBottom: '10px' }} />
              <div className="skeleton skeleton-row" style={{ height: '55px', marginBottom: '10px' }} />
              <div className="skeleton skeleton-row" style={{ height: '55px', marginBottom: '10px' }} />
              <div className="skeleton skeleton-row" style={{ height: '55px', marginBottom: '10px' }} />
            </div>
          ) : bills.length === 0 ? (
            <div className="empty-bills-state">
              <span className="empty-icon">📁</span>
              <h4>No Bills Uploaded Yet</h4>
              <p>Uploaded customer invoices will be listed here.</p>
            </div>
          ) : (
            <>
              <div className="table-responsive-wrapper">
                <table className="admin-bills-table">
                  <thead>
                    <tr>
                      <th style={{ width: '70px' }}>Thumbnail</th>
                      <th>Invoice No.</th>
                      <th>Customer Name</th>
                      <th>Shop Name</th>
                      <th>Order No.</th>
                      <th>Uploaded On</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bills.map((bill) => (
                      <tr key={bill._id}>
                        {/* Thumbnail */}
                        <td>
                          <div 
                            className="admin-bill-thumb-wrapper"
                            onClick={() => {
                              setPreviewBill(bill);
                              setZoomScale(1);
                            }}
                            title="Preview Invoice"
                          >
                            <img src={bill.imageUrl} alt={bill.invoiceNumber} className="admin-bill-thumb" />
                          </div>
                        </td>
                        {/* Invoice Number */}
                        <td>
                          <strong className="invoice-highlight">{bill.invoiceNumber}</strong>
                        </td>
                        {/* Customer Name */}
                        <td>{bill.userId ? bill.userId.name : <span style={{ color: '#ef4444' }}>Deleted User</span>}</td>
                        {/* Shop Name */}
                        <td>{bill.userId && bill.userId.shopName ? bill.userId.shopName : '-'}</td>
                        {/* Order Number */}
                        <td>
                          {bill.orderId ? (
                            <span className="linked-order-badge">
                              {bill.orderId.orderNumber || bill.orderId._id.substring(18)}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                          )}
                        </td>
                        {/* Upload Date */}
                        <td>{formatDate(bill.createdAt)}</td>
                        {/* Actions */}
                        <td>
                          <div className="action-buttons-cell">
                            <button 
                              className="action-btn view-btn"
                              onClick={() => {
                                setPreviewBill(bill);
                                setZoomScale(1);
                              }}
                              title="Quick View"
                            >
                              <Eye size={16} />
                            </button>
                            <button 
                              className="action-btn delete-btn"
                              onClick={() => handleDeleteBill(bill._id)}
                              title="Delete Invoice"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {pages > 1 && (
                <div className="admin-pagination-controls">
                  <button 
                    disabled={page === 1} 
                    onClick={() => fetchBills(page - 1)}
                    className="pagination-btn"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="pagination-text">Page {page} of {pages}</span>
                  <button 
                    disabled={page === pages} 
                    onClick={() => fetchBills(page + 1)}
                    className="pagination-btn"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

      </div>

      {/* FULL PREVIEW & ZOOM OVERLAY MODAL */}
      {previewBill && (
        <div className="modal-overlay" onClick={() => setPreviewBill(null)}>
          <div className="modal-container preview-bill-modal" onClick={(e) => e.stopPropagation()}>
            
            <div className="modal-header">
              <div>
                <h3 style={{ margin: 0 }}>Invoice Details</h3>
                <span className="modal-bill-meta">
                  No: {previewBill.invoiceNumber} | Customer: {previewBill.userId ? previewBill.userId.name : 'Unknown'}
                </span>
              </div>
              <button onClick={() => setPreviewBill(null)} className="modal-close-btn">&times;</button>
            </div>
            
            <div className="modal-body preview-bill-body">
              {/* Zoom and External Links control bar */}
              <div className="modal-control-bar">
                <button 
                  className="control-btn" 
                  onClick={() => setZoomScale(prev => Math.min(prev + 0.25, 2.5))}
                  title="Zoom In"
                >
                  <ZoomIn size={18} />
                </button>
                <button 
                  className="control-btn" 
                  onClick={() => setZoomScale(prev => Math.max(prev - 0.25, 0.5))}
                  title="Zoom Out"
                >
                  <ZoomOut size={18} />
                </button>
                <button 
                  className="control-btn" 
                  onClick={() => setZoomScale(1)}
                  title="Reset Zoom"
                >
                  100%
                </button>
                <a 
                  href={previewBill.imageUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="control-link-btn"
                  title="Open Original Image in New Tab"
                >
                  <ExternalLink size={18} /> Open Original
                </a>
              </div>

              {/* Image box with scaling style */}
              <div className="bill-image-scroll-pane">
                <img 
                  src={previewBill.imageUrl} 
                  alt={previewBill.invoiceNumber} 
                  style={{ transform: `scale(${zoomScale})`, transformOrigin: 'top center' }} 
                  className="zoomable-bill-img"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => setPreviewBill(null)} className="btn">
                Close Preview
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default AdminBills;
